// Account service for managing client accounts
import { 
  collection, 
  addDoc, 
  getDocs, 
  getDoc,
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  where 
} from 'firebase/firestore';
import { db } from '../firebase';

class AccountService {
  constructor() {
    this.collectionName = 'accounts';
  }

  // Create a new account
  async createAccount(accountData) {
    try {
      const accountWithTimestamp = {
        ...accountData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      const docRef = await addDoc(collection(db, this.collectionName), accountWithTimestamp);
      
      return { id: docRef.id, ...accountWithTimestamp };
    } catch (error) {
      console.error('Error creating account:', error);
      throw error;
    }
  }

  // Get all accounts
  async getAllAccounts() {
    try {
      const q = query(collection(db, this.collectionName));
      const querySnapshot = await getDocs(q);
      const accounts = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        accounts.push({
          id: doc.id,
          ...data
        });
      });

      // Sort by name (handle cases where name might be undefined)
      accounts.sort((a, b) => {
        const nameA = a.name || '';
        const nameB = b.name || '';
        return nameA.localeCompare(nameB);
      });

      return accounts;
    } catch (error) {
      console.error('Error getting accounts:', error);
      return [];
    }
  }

  // Get account by ID
  async getAccountById(accountId) {
    try {
      const accountRef = doc(db, this.collectionName, accountId);
      const accountDoc = await getDoc(accountRef);
      
      if (!accountDoc.exists()) {
        return null;
      }

      return {
        id: accountDoc.id,
        ...accountDoc.data()
      };
    } catch (error) {
      console.error('Error getting account by ID:', error);
      return null;
    }
  }

  // Update an account
  async updateAccount(accountId, accountData) {
    try {
      const updateData = {
        ...accountData,
        updatedAt: new Date().toISOString()
      };

      const accountRef = doc(db, this.collectionName, accountId);
      await updateDoc(accountRef, updateData);

      return { id: accountId, ...updateData };
    } catch (error) {
      console.error('Error updating account:', error);
      throw error;
    }
  }

  // Delete an account
  async deleteAccount(accountId) {
    try {
      const accountRef = doc(db, this.collectionName, accountId);
      await deleteDoc(accountRef);
      
      return true;
    } catch (error) {
      console.error('Error deleting account:', error);
      throw error;
    }
  }

  // Search accounts
  async searchAccounts(searchTerm) {
    try {
      const q = query(collection(db, this.collectionName));
      const querySnapshot = await getDocs(q);
      const accounts = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const searchableText = `${data.name} ${data.contactEmail} ${data.city} ${data.state}`.toLowerCase();
        
        if (searchableText.includes(searchTerm.toLowerCase())) {
          accounts.push({
            id: doc.id,
            ...data
          });
        }
      });

      return accounts;
    } catch (error) {
      console.error('Error searching accounts:', error);
      return [];
    }
  }

  // Get accounts by status
  async getAccountsByStatus(status) {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('status', '==', status)
      );

      const querySnapshot = await getDocs(q);
      const accounts = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        accounts.push({
          id: doc.id,
          ...data
        });
      });

      return accounts;
    } catch (error) {
      console.error('Error getting accounts by status:', error);
      return [];
    }
  }
}

const accountService = new AccountService();
export default accountService;
